import React from 'react';

import {useGate, useUnit} from 'effector-react';
import {useTranslation} from 'react-i18next';

import {AuditStatisticsGate, auditStatisticsModel} from '@entities/audit-statistics/model';
import {Icon} from '@shared/ui/Icon';
import {Select, SelectOption} from '@shared/ui/Select';

import {getLocalizedTitle} from './charts/chartUtils';
import {NotificationsByStatusChart} from './charts/NotificationsByStatusChart';
import {TaxPayersByViolationStatusChart} from './charts/TaxPayersByViolationStatusChart';
import {ViolationsByTypeChart} from './charts/ViolationsByTypeChart';
import {ViolationsByTypeTrendChart} from './charts/ViolationsByTypeTrendChart';
import {
    Card,
    Filters,
    ValueCaption,
    ValueCard,
    ValueCards,
    ValueIcon,
    ValueNumber,
    ValueTitle,
    Wrapper,
} from './styled';

interface ValueCardConfig {
    title: string;
    value: string;
    amount?: string;
    icon: 'taxGap' | 'attention' | 'mail';
    background: string;
}

const StatisticValueCard = ({title, value, amount, icon, background}: ValueCardConfig) => {
    const {t} = useTranslation();

    return (
        <ValueCard>
            <ValueIcon background={background}>
                <Icon name={icon} size={24} color="#59606A" />
            </ValueIcon>
            <div>
                <ValueTitle>{title}</ValueTitle>
                <ValueNumber>{value}</ValueNumber>
                {amount && (
                    <ValueCaption>
                        {t('page_audit.page_statistic.in-the-amount-of', {amount})}
                    </ValueCaption>
                )}
            </div>
        </ValueCard>
    );
};

export const AuditStatisticsTab = () => {
    const {t, i18n} = useTranslation();
    const state = useUnit(auditStatisticsModel.state);

    useGate(AuditStatisticsGate);

    const violationTypeOptions = React.useMemo<Array<SelectOption<string>>>(
        () =>
            state.violationTypes.map((item) => ({
                value: item.id,
                label: getLocalizedTitle(item, i18n.language) || item.code,
            })),
        [i18n.language, state.violationTypes],
    );
    const selectedViolationTypes = violationTypeOptions.filter((item) =>
        state.selectedViolationTypeIds.includes(item.value ?? ''),
    );
    const statistics = state.statistics;
    const valueCards: ValueCardConfig[] = [
        {
            title: t('page_audit.page_statistic.found-violations-count'),
            value: statistics.foundViolationsCountNumGroup,
            amount: statistics.foundViolationsSumNumGroup,
            icon: 'taxGap',
            background: '#FFE8E8',
        },
        {
            title: t('page_audit.page_statistic.legal-persons-with-opened-violations'),
            value: statistics.legalPersonsWithOpenedViolationsNumGroup,
            amount: statistics.legalPersonsWithOpenedViolationsSumNumGroup,
            icon: 'attention',
            background: '#DEF1FF',
        },
        {
            title: t('page_audit.page_statistic.legal-persons-with-closed-violations'),
            value: statistics.legalPersonsWithClosedViolationsNumGroup,
            amount: statistics.legalPersonsWithClosedViolationsSumNumGroup,
            icon: 'attention',
            background: '#E1E8EF',
        },
        {
            title: t('page_audit.page_statistic.notifications-sent'),
            value: statistics.notificationsSentNumGroup,
            icon: 'mail',
            background: '#FFF5C4',
        },
        {
            title: t('page_audit.page_statistic.closed-violations-count'),
            value: statistics.closedViolationsCountNumGroup,
            amount: statistics.closedViolationsSumNumGroup,
            icon: 'taxGap',
            background: '#E2FFD7',
        },
    ];

    return (
        <Wrapper>
            <Card>
                <Filters>
                    <Select
                        isMulti
                        size="sm"
                        options={violationTypeOptions}
                        value={selectedViolationTypes}
                        closeMenuOnSelect={false}
                        placeholder={t('page_audit.terms.violation-type')}
                        isLoading={state.pending}
                        onChange={(value: readonly SelectOption<string>[]) =>
                            auditStatisticsModel.violationTypesChanged(
                                value
                                    .map((item) => item.value)
                                    .filter((item): item is string => Boolean(item)),
                            )
                        }
                    />
                </Filters>
                <ValueCards>
                    {valueCards.map((item) => (
                        <StatisticValueCard key={item.title} {...item} />
                    ))}
                </ValueCards>
            </Card>

            <Card>
                <TaxPayersByViolationStatusChart
                    statistics={statistics}
                    violationStatuses={state.violationStatuses}
                />
            </Card>

            <Card>
                <ViolationsByTypeChart
                    statistics={statistics}
                    violationTypes={state.violationTypes}
                />
            </Card>

            <Card>
                <ViolationsByTypeTrendChart
                    statistics={statistics}
                    violationTypes={state.violationTypes}
                />
            </Card>

            <Card>
                <NotificationsByStatusChart
                    statistics={statistics}
                    notificationStatuses={state.notificationStatuses}
                />
            </Card>
        </Wrapper>
    );
};
